import { useState } from "react";
import { T } from "../utils/theme";
import { ENDPOINTS } from "../utils/api";
import { useFetch } from "../hooks";
import { Card, Spinner, EmptyState, SectionHeader, SearchInput, ProgressBar } from "../components/UI";
import { Star } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

const MEDALS = ["🥇", "🥈", "🥉"];

// ─── Normalisation de la réponse API ─────────────────────────────────────────
// L'API retourne : { rank, hacker: { id, pseudo, niveau, points, user: { email, ... } } }
// On aplatit tout pour que le reste du composant accède directement aux champs.
const normalizeRanking = (item) => ({
  rank:             item.rank,
  id:               item.hacker?.id,
  pseudo:           item.hacker?.pseudo           ?? "—",
  nom:              item.hacker?.nom              ?? null,
  prenom:           item.hacker?.prenom           ?? null,
  avatar:           item.hacker?.avatar           ?? null,
  niveau:           item.hacker?.niveau           ?? null,
  points:           item.hacker?.points           ?? 0,
  // données user
  email:            item.hacker?.user?.email      ?? null,
  verified:         item.hacker?.user?.verified   ?? false,
  docSet:           item.hacker?.user?.docSet     ?? false,
  kycVerifie:       item.hacker?.user?.kycVerifie ?? false,
  statutCompte:     item.hacker?.user?.statutCompte ?? null,
  createdAt:        item.hacker?.user?.createdAt  ?? null,
});

// ─── Main Page ────────────────────────────────────────────────────────────────
const RankingPage = ({ token }) => {
  const { data: raw, loading } = useFetch(ENDPOINTS.adminRanking, token);
  const [search, setSearch]    = useState("");

  // Normalisation : raw est un tableau de { rank, hacker: {...} }
  const rawList = Array.isArray(raw) ? raw.map(normalizeRanking) : [];

  const list = rawList.filter(h =>
    !search || h.pseudo?.toLowerCase().includes(search.toLowerCase())
  );

  const top10 = list.slice(0, 10);

  return (
    <div className="fade-up" style={{ padding: "24px 28px", overflowY: "auto", flex: 1 }}>
      <SectionHeader
        title="Classement des Hackers"
        sub="Hall of Fame — Top contributeurs de la plateforme"
        action={<SearchInput value={search} onChange={setSearch} style={{ width: 220 }} />}
      />

      {loading ? <Spinner /> : list.length === 0 ? (
        <EmptyState icon={Star} title="Aucun classement" />
      ) : (
        <>
          {/* ── Top 3 Podium ─────────────────────────────────────────────────── */}
          <div style={{
            display: "flex", gap: 16, marginBottom: 24,
            alignItems: "flex-end", justifyContent: "center",
          }}>
            {/* Ordre podium : 2ème - 1er - 3ème */}
            {[list[1], list[0], list[2]].map((h, i) => {
              if (!h) return <div key={i} style={{ flex: 1 }} />;
              const rank      = i === 1 ? 1 : i === 0 ? 2 : 3;
              const heights   = [110, 140, 95];
              const mainColor = rank === 1 ? T.yellow : rank === 2 ? "#c0c0c0" : "#cd7f32";

              return (
                <div key={i} style={{ flex: 1, textAlign: "center", maxWidth: 220 }}>
                  <div style={{ marginBottom: 10 }}>
                    <div style={{
                      width: 52, height: 52, borderRadius: "50%", margin: "0 auto 8px",
                      background: `linear-gradient(135deg, ${mainColor}, ${mainColor}88)`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 20, fontWeight: 900, color: "#000",
                      boxShadow: `0 0 20px ${mainColor}60`,
                    }}>
                      {(h.pseudo || "?")[0].toUpperCase()}
                    </div>
                    <div style={{ fontFamily: T.display, fontWeight: 800, fontSize: 14, color: T.text }}>
                      {h.pseudo}
                    </div>
                    <div style={{ fontSize: 11, color: T.muted }}>{h.niveau || "—"}</div>
                  </div>
                  <div style={{
                    height: heights[i],
                    background: `${mainColor}15`,
                    border: `2px solid ${mainColor}40`,
                    borderBottom: "none",
                    borderRadius: "10px 10px 0 0",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexDirection: "column",
                  }}>
                    <div style={{ fontSize: 26 }}>{MEDALS[rank - 1]}</div>
                    <div style={{ fontFamily: T.display, fontWeight: 900, fontSize: 20, color: mainColor }}>
                      {(h.points || 0).toLocaleString()}
                    </div>
                    <div style={{ fontSize: 10, color: T.muted }}>points</div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* ── Chart Top 10 ─────────────────────────────────────────────────── */}
          {top10.length > 0 && (
            <Card style={{ padding: 22, marginBottom: 20 }}>
              <div style={{ fontFamily: T.display, fontWeight: 700, fontSize: 14, marginBottom: 4 }}>Top 10 — Points</div>
              <div style={{ fontSize: 11, color: T.muted, marginBottom: 16 }}>Comparaison des meilleurs hackers</div>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={top10} barSize={28}>
                  <XAxis
                    dataKey="pseudo"
                    tick={{ fill: T.muted, fontSize: 10 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis hide />
                  <Tooltip
                    contentStyle={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, fontSize: 12 }}
                    formatter={(v) => [v.toLocaleString() + " pts", "Points"]}
                  />
                  <Bar dataKey="points" radius={[5, 5, 0, 0]}>
                    {top10.map((_, i) => (
                      <Cell key={i} fill={i === 0 ? T.yellow : i < 3 ? T.blue : T.purple} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
          )}

          {/* ── Classement complet ───────────────────────────────────────────── */}
          <Card>
            <div style={{ padding: "14px 20px", borderBottom: `1px solid ${T.border}` }}>
              <div style={{ fontFamily: T.display, fontWeight: 700, fontSize: 14 }}>Classement Complet</div>
            </div>

            {list.map((h, i) => {
              const maxPts     = list[0]?.points || 1;
              const levelColor = {
                "Expert":        T.blue,
                "Intermediaire": T.purple,
                "Intermédiaire": T.purple,
                "Novice":        T.muted,
              }[h.niveau] || T.muted;

              return (
                <div key={h.id || i} className="hov-row" style={{
                  display: "flex", alignItems: "center", gap: 14, padding: "13px 20px",
                  borderBottom: `1px solid ${T.border}22`,
                  background: i < 3
                    ? [`${T.yellow}06`, `${T.muted}04`, `${T.orange}05`][i]
                    : "transparent",
                }}>
                  {/* Rang */}
                  <div style={{ width: 36, fontSize: 16, textAlign: "center", flexShrink: 0 }}>
                    {i < 3
                      ? MEDALS[i]
                      : <span style={{ fontFamily: T.display, fontWeight: 800, fontSize: 14, color: T.muted }}>#{h.rank}</span>}
                  </div>

                  {/* Avatar */}
                  <div style={{
                    width: 38, height: 38, borderRadius: "50%", flexShrink: 0,
                    background: `hsl(${(h.pseudo || "?").charCodeAt(0) * 17 % 360}, 50%, 35%)`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 14, fontWeight: 900, color: "#fff",
                    border: i === 0 ? `2px solid ${T.yellow}` : "none",
                  }}>
                    {(h.pseudo || "?")[0].toUpperCase()}
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                      <span style={{ fontFamily: T.display, fontWeight: 700, fontSize: 14, color: i === 0 ? T.yellow : T.text }}>
                        {h.pseudo}
                      </span>
                      {h.niveau && (
                        <span className="badge" style={{ color: levelColor, background: `${levelColor}12` }}>
                          {h.niveau}
                        </span>
                      )}
                      {/* Badge KYC vérifié */}
                      {h.kycVerifie && (
                        <span className="badge" style={{ color: T.green, background: `${T.green}12` }}>
                          ✓ KYC
                        </span>
                      )}
                    </div>
                    <ProgressBar value={h.points || 0} max={maxPts} color={i === 0 ? T.yellow : levelColor} height={4} />
                    {/* Email affiché pour l'admin */}
                    {h.email && (
                      <div style={{ fontSize: 10, color: T.muted, marginTop: 3 }}>{h.email}</div>
                    )}
                  </div>

                  {/* Points */}
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ fontFamily: T.display, fontWeight: 900, fontSize: 18, color: i === 0 ? T.yellow : T.text }}>
                      {(h.points || 0).toLocaleString()}
                    </div>
                    <div style={{ fontSize: 9, color: T.muted }}>points</div>
                  </div>
                </div>
              );
            })}
          </Card>
        </>
      )}
    </div>
  );
};

export default RankingPage;